import { brand } from 'brands';
import { readRuntimeConfig } from 'functions/runtimeConfig';
import { ServiceIds } from '@utils/serviceIds';
import type { DcbUserResponse } from '../types/dcb';

/**
 * Gated demo mock-auth for the MTN client demo.
 *
 * We have no MTN productId/serviceId yet, and the real Vodacom flow would send
 * Chess-Masters-branded SMS. So for the two whitelisted demo numbers we skip the
 * real DCB user lookup entirely and inject a mock user. The gameplay token still
 * comes from trivia-v2 (its OtpTestBypass whitelist accepts OTP "0000" for these
 * same numbers) — this only fakes the client-side user object.
 *
 * VIP vs free is purely the user's `status`:
 *   - 'Active'  → unlimited play, routes straight to /home
 *   - 'Pending' → free user: limited daily plays (our own Mongo), still /home
 *
 * Triple-gated so it can never affect a real deployment: the pod must run with
 * DEMO_MODE=true, the brand must be MTN, and the MSISDN must be whitelisted.
 * Keep DEMO_MODE unset for any real deployment.
 */

// Reuse the real Chess Masters service so genuine questions/lessons load — MTN
// shares the platform, only the assets differ. Swap when MTN gets its own service.
const DEMO_SERVICE_ID = ServiceIds.daily;

// MSISDNs are the sanitized 27… form (sanitizeMsisdn turns 0836230796 → 27836230796).
// This list must mirror the OtpTestBypass:Msisdns whitelist configured on trivia-v2.
const DEMO_USERS: Record<string, DcbUserResponse> = {
    '27836230796': {
        msisdn: '27836230796',
        serviceId: DEMO_SERVICE_ID,
        status: 'Active', // VIP — unlimited
        username: 'MTN VIP',
        subscriptionId: 'demo-vip',
        productId: 36,
    },
    '27836230795': {
        msisdn: '27836230795',
        serviceId: DEMO_SERVICE_ID,
        status: 'Pending', // Free — limited daily plays
        username: 'MTN Player',
        subscriptionId: null,
        productId: 36,
    },
};

/** Demo mode is on only for an MTN deployment explicitly run with DEMO_MODE=true. */
export function isDemoModeEnabled(): boolean {
    return readRuntimeConfig().demoMode && brand.id === 'mtn-checkmate-rush';
}

/**
 * Returns a mock user for a whitelisted demo MSISDN when demo mode is on, else null.
 * Used to short-circuit the real DCB user lookup on both initial login and session
 * restore (both funnel through DcbService.getUserViaProductId).
 */
export function getDemoUser(msisdn: string | undefined | null): DcbUserResponse | null {
    if (!isDemoModeEnabled() || !msisdn) return null;
    return DEMO_USERS[msisdn] ?? null;
}
